import { useEffect, useRef, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { Link, useParams, useLocation } from 'react-router-dom';

const NavTabs = ({ items = [], className = '', autoRevealActive = false, trackOverflow = false }) => {
    const { id } = useParams();
    const location = useLocation();
    const navRef = useRef(null);
    const [overflow, setOverflow] = useState({ left: false, right: false });

    useEffect(() => {
        if (!trackOverflow) return undefined;

        const nav = navRef.current;
        if (!nav) return undefined;
        let cancelled = false;

        const updateOverflow = () => {
            if (cancelled) return;

            const nextOverflow = {
                left: nav.scrollLeft > 1,
                right: nav.scrollWidth - nav.clientWidth - nav.scrollLeft > 1,
            };

            setOverflow((current) => (
                current.left === nextOverflow.left && current.right === nextOverflow.right
                    ? current
                    : nextOverflow
            ));
        };

        const animationFrame = window.requestAnimationFrame(updateOverflow);
        const resizeObserver = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(updateOverflow);

        nav.addEventListener('scroll', updateOverflow, { passive: true });
        resizeObserver?.observe(nav);
        window.addEventListener('resize', updateOverflow);
        document.fonts?.ready.then(updateOverflow);

        return () => {
            cancelled = true;
            window.cancelAnimationFrame(animationFrame);
            nav.removeEventListener('scroll', updateOverflow);
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateOverflow);
        };
    }, [items.length, trackOverflow]);

    useEffect(() => {
        if (!autoRevealActive) return undefined;

        let cancelled = false;

        const revealActive = () => {
            const nav = navRef.current;
            const activeLink = nav?.querySelector('.nav-link.active');

            if (!nav || !activeLink) return;

            const navRect = nav.getBoundingClientRect();
            const activeRect = activeLink.getBoundingClientRect();
            // navRect spans the nav's border-box, so aligning to it flush leaves
            // no room for the nav's own end padding — pull in by that amount so
            // the revealed tab keeps the same breathing room as the other edge.
            const navStyle = window.getComputedStyle(nav);
            const paddingLeft = parseFloat(navStyle.paddingLeft) || 0;
            const paddingRight = parseFloat(navStyle.paddingRight) || 0;
            let horizontalOffset = 0;

            if (activeRect.left < navRect.left + paddingLeft) {
                horizontalOffset = activeRect.left - (navRect.left + paddingLeft);
            } else if (activeRect.right > navRect.right - paddingRight) {
                horizontalOffset = activeRect.right - (navRect.right - paddingRight);
            }

            if (horizontalOffset !== 0) {
                nav.scrollTo({
                    left: nav.scrollLeft + horizontalOffset,
                    behavior: 'smooth',
                });
            }
        };

        const animationFrame = window.requestAnimationFrame(revealActive);

        // Web fonts (e.g. Inter) can still be loading at that first check, so the
        // tab labels measured then are narrower than their final rendered width.
        // Re-check once fonts finish loading, or the active tab can end up cut
        // off with no further recalculation to catch it.
        document.fonts?.ready.then(() => {
            if (!cancelled) revealActive();
        });

        return () => {
            cancelled = true;
            window.cancelAnimationFrame(animationFrame);
        };
    }, [autoRevealActive, location.pathname]);

    if (!Array.isArray(items) || items.length === 0) return null;

    return (
        <Nav
            ref={navRef}
            variant="pills"
            className={`${className}${overflow.left ? ' has-overflow-left' : ''}${overflow.right ? ' has-overflow-right' : ''}`}
        >
            {items.map(({ to, eventKey, label, disabled, end, alsoActive }) => {
                const resolvedTo = id ? to.replace(':id', id) : to;
                const isActive = end
                    ? location.pathname === resolvedTo
                    : (location.pathname === resolvedTo || location.pathname.startsWith(resolvedTo + '/'))
                    || (alsoActive?.some(p => location.pathname === p || location.pathname.startsWith(p + '/')));

                return (
                    <Nav.Item key={eventKey}>
                        <Nav.Link
                            as={disabled ? 'span' : Link}
                            to={disabled ? undefined : resolvedTo}
                            eventKey={eventKey}
                            active={isActive}
                            className={disabled ? 'disabled text-muted' : ''}
                            style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                        >
                            {label}
                        </Nav.Link>
                    </Nav.Item>
                );
            })}
        </Nav>
    );
};

export default NavTabs;
